import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  NavLink,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import Banners from "./pages/Banners";
import Duplicates from "./pages/Duplicates";
import Generate from "./pages/Generate";
import Logs from "./pages/Logs";
import Notifications from "./pages/Notifications";
import Products from "./pages/Products";
import Promocodes from "./pages/Promocodes";
import Recipes from "./pages/Recipes";
import Subscriptions from "./pages/Subscriptions";
import Substitutions from "./pages/Substitutions";
import Support from "./pages/Support";
import Team from "./pages/Team";
import Users from "./pages/Users";
import { supabase } from "./supabase";

export default function App() {
  const [session, setSession] = useState<any>(null);
  const [adminData, setAdminData] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkAdminAccess(session.user.email);
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) checkAdminAccess(session.user.email);
      else setAdminData(null);
    });
  }, []);

  const checkAdminAccess = async (email?: string) => {
    if (!email) return;
    setCheckingAdmin(true);
    const { data } = await supabase
      .from("admin_users")
      .select("*")
      .eq("email", email.toLowerCase().trim())
      .eq("is_active", true)
      .maybeSingle();
    if (!data) {
      await supabase.auth.signOut();
      setError("Доступ запрещён. Обратитесь к владельцу.");
    } else {
      setAdminData(data);
    }
    setCheckingAdmin(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const hasAccess = (section: string) => {
    if (!adminData) return false;
    if (adminData.role === "owner") return true;
    return adminData.permissions?.includes(section);
  };

  if (!session || !adminData) {
    return (
      <div style={styles.loginPage}>
        <div style={styles.loginCard}>
          <h1 style={styles.loginTitle}>🍳 Kitchery Admin</h1>
          <p style={styles.loginSubtitle}>Панель управления</p>
          {checkingAdmin ? (
            <p style={{ textAlign: "center", color: "#888" }}>
              Проверка доступа...
            </p>
          ) : (
            <form onSubmit={handleLogin}>
              <input
                style={styles.input}
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                style={styles.input}
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {error && <p style={styles.error}>{error}</p>}
              <button style={styles.loginBtn} type="submit" disabled={loading}>
                {loading ? "Входим..." : "Войти"}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  const NAV_ITEMS = [
    { key: "recipes", label: "📖 Рецепты", path: "/recipes" },
    { key: "products", label: "🥕 Продукты", path: "/products" },
    { key: "users", label: "👥 Пользователи", path: "/users" },
    { key: "subscriptions", label: "💳 Подписки", path: "/subscriptions" },
    { key: "substitutions", label: "🔄 Замены", path: "/substitutions" },
    { key: "generate", label: "✨ ИИ Генерация", path: "/generate" },
    { key: "support", label: "💬 Поддержка", path: "/support" },
    { key: "logs", label: "📋 Логи", path: "/logs" },
    { key: "notifications", label: "🔔 Уведомления", path: "/notifications" },
    { key: "promocodes", label: "🎟️ Промокоды", path: "/promocodes" },
    { key: "banners", label: "🖼️ Баннеры", path: "/banners" },
    { key: "duplicates", label: "🔍 Дубликаты", path: "/duplicates" },
    { key: "team", label: "👑 Команда", path: "/team" },
  ];

  return (
    <BrowserRouter>
      <div style={styles.layout}>
        <nav style={styles.sidebar}>
          <div style={styles.logo}>🍳 Kitchery</div>
          {NAV_ITEMS.filter((item) => hasAccess(item.key)).map((item) => (
            <NavLink
              key={item.key}
              to={item.path}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
            >
              {item.label}
            </NavLink>
          ))}
          <div style={styles.ownerBadge}>
            {adminData.role === "owner" ? "👑" : "🔧"} {session.user.email}
          </div>
          <button
            style={styles.logoutBtn}
            onClick={() => supabase.auth.signOut()}
          >
            Выйти
          </button>
        </nav>
        <main style={styles.main}>
          <Routes>
            <Route path="/" element={<Navigate to="/recipes" />} />
            {hasAccess("recipes") && (
              <Route path="/recipes" element={<Recipes />} />
            )}
            {hasAccess("products") && (
              <Route path="/products" element={<Products />} />
            )}
            {hasAccess("users") && <Route path="/users" element={<Users />} />}
            {hasAccess("subscriptions") && (
              <Route path="/subscriptions" element={<Subscriptions />} />
            )}
            {hasAccess("substitutions") && (
              <Route path="/substitutions" element={<Substitutions />} />
            )}
            {hasAccess("generate") && (
              <Route path="/generate" element={<Generate />} />
            )}
            {hasAccess("support") && (
              <Route path="/support" element={<Support />} />
            )}
            {hasAccess("logs") && <Route path="/logs" element={<Logs />} />}
            {hasAccess("notifications") && (
              <Route path="/notifications" element={<Notifications />} />
            )}
            {hasAccess("promocodes") && (
              <Route path="/promocodes" element={<Promocodes />} />
            )}
            {hasAccess("banners") && (
              <Route path="/banners" element={<Banners />} />
            )}
            {hasAccess("duplicates") && (
              <Route path="/duplicates" element={<Duplicates />} />
            )}
            {hasAccess("team") && <Route path="/team" element={<Team />} />}
            <Route path="*" element={<Navigate to="/recipes" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loginPage: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
  },
  loginCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 40,
    width: 360,
    boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
  },
  loginTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "#2ECC71",
    margin: "0 0 8px",
    textAlign: "center",
  },
  loginSubtitle: {
    fontSize: 14,
    color: "#888",
    textAlign: "center",
    marginBottom: 24,
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: 10,
    border: "1px solid #eee",
    fontSize: 15,
    marginBottom: 12,
    boxSizing: "border-box",
    outline: "none",
  },
  error: { color: "#e74c3c", fontSize: 13, marginBottom: 12 },
  loginBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#2ECC71",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
  },
  layout: {
    display: "flex",
    minHeight: "100vh",
    fontFamily: "-apple-system, sans-serif",
  },
  sidebar: {
    width: 220,
    backgroundColor: "#1a1a2e",
    display: "flex",
    flexDirection: "column",
    padding: "24px 0",
    position: "fixed",
    height: "100vh",
    overflowY: "auto",
  },
  logo: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2ECC71",
    padding: "0 24px 24px",
    borderBottom: "1px solid #333",
    marginBottom: 8,
  },
  navLink: {
    display: "block",
    padding: "12px 24px",
    color: "#aaa",
    textDecoration: "none",
    fontSize: 14,
    marginTop: 4,
  },
  navLinkActive: {
    color: "#2ECC71",
    backgroundColor: "rgba(46,204,113,0.1)",
    borderRight: "3px solid #2ECC71",
  },
  ownerBadge: {
    margin: "auto 24px 8px",
    fontSize: 11,
    color: "#555",
    wordBreak: "break-all",
  },
  logoutBtn: {
    marginLeft: 24,
    marginRight: 24,
    marginBottom: 16,
    padding: "10px",
    backgroundColor: "transparent",
    border: "1px solid #444",
    borderRadius: 8,
    color: "#aaa",
    cursor: "pointer",
    fontSize: 13,
  },
  main: {
    marginLeft: 220,
    flex: 1,
    padding: 32,
    backgroundColor: "#f9f9f9",
    minHeight: "100vh",
  },
};
